import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
} from "@mui/x-data-grid-premium";
import { AccountPageLayout } from "components/common/AccountPageLayout";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid/HotkeyCompliantDataGrid";
import { useTenantData } from "contexts/tenant";
import { useTenantTranslation } from "hooks/formatters";
import { usePermission } from "hooks/permissions";
import { useTenant } from "hooks/settings";
import { ReactNode, useMemo, useState } from "react";
import { Link } from "react-router";
import {
  ActiveHeatOverride,
  HeatQueueEntry,
  HeatRecipe,
  useGetHeatQueueQuery,
} from "src/store/api/generatedApi";
import { DeltaOverrideDialog } from "./delta/DeltaOverrideDialog";
import {
  HEAT_GRID_SX,
  HeatRow,
  QUEUE_START_TIME,
  toHeatRow,
  useHeatDataColumns,
} from "./heatColumns";
import { HeatStatusPill } from "./HeatStatusPill";
import { MeltPlanStaleAlert, MeltPlanUpdatedAt } from "./MeltPlanFreshness";
import { OVERRIDE_GUIDE_LINK_LABEL, OVERRIDE_GUIDE_PATH } from "./guide/routes";
import {
  DELTA_OVERRIDES_TENANT_SETTINGS,
  EDIT_HEAT_RECIPE_PERMISSIONS,
} from "./permissions";
import {
  OverrideEndBanner,
  OverrideStartBanner,
} from "./override/OverrideInterstitial";
import { OverrideRecipeModal } from "./override/OverrideRecipeModal";
import { useSummariseOverrideCosts } from "./override/useOverrideCostSummaries";
import { useContiguousHeatSelection } from "./override/useContiguousHeatSelection";
import { usePreloadOverrideRecipeModal } from "./override/usePreloadOverrideRecipeModal";

const POLLING_INTERVAL_MS = 60_000;

// Full-width banner rows bracketing each contiguous run of overridden heats.
interface OverrideStartRow {
  kind: "override-start";
  id: string;
  override: ActiveHeatOverride;
  heatCount: number;
  claimedHeatIds: number[];
}

interface OverrideEndRow {
  kind: "override-end";
  id: string;
  endHeatId: number;
}

type QueueRow = HeatRow | OverrideStartRow | OverrideEndRow;

// Interleave heat rows with start/end banner rows around each maximal run of
// consecutive heats belonging to the same override. Non-contiguous overrides
// (or two different overrides side by side) yield separate banner pairs.
const buildRows = (
  heats: HeatQueueEntry[],
  overrides: ActiveHeatOverride[]
): QueueRow[] => {
  const overrideById = new Map(
    overrides.map((override) => [override.override_id, override])
  );
  const claimedHeatIds = (overrideId: number): number[] =>
    heats
      .filter((heat) => heat.override_id === overrideId)
      .map((heat) => heat.heat_id);

  interface OverrideBlock {
    override: ActiveHeatOverride;
    heatRows: HeatRow[];
    firstHeatId: number;
    lastHeatId: number;
  }

  const rows: QueueRow[] = [];
  let block: OverrideBlock | null = null;

  const flushBlock = () => {
    if (block === null) {
      return;
    }
    rows.push({
      kind: "override-start",
      id: `start:${block.override.override_id}:${block.firstHeatId}`,
      override: block.override,
      heatCount: block.heatRows.length,
      claimedHeatIds: claimedHeatIds(block.override.override_id),
    });
    rows.push(...block.heatRows);
    rows.push({
      kind: "override-end",
      id: `end:${block.override.override_id}:${block.lastHeatId}`,
      endHeatId: block.lastHeatId,
    });
    block = null;
  };

  for (const heat of heats) {
    const override =
      heat.override_id === null
        ? undefined
        : overrideById.get(heat.override_id);
    if (override === undefined) {
      flushBlock();
      rows.push(toHeatRow(heat));
      continue;
    }
    if (block !== null && block.override !== override) {
      flushBlock();
    }
    block ??= {
      override,
      heatRows: [],
      firstHeatId: heat.heat_id,
      lastHeatId: heat.heat_id,
    };
    block.heatRows.push(toHeatRow(heat));
    block.lastHeatId = heat.heat_id;
  }
  flushBlock();
  return rows;
};

// A data-column renderer that only runs for heat rows; banner rows are covered
// by the status column's colSpan, so their other cells are never rendered.
const heatCell =
  (render: (row: HeatRow) => ReactNode) =>
  ({ row }: GridRenderCellParams<QueueRow>): ReactNode =>
    row.kind === "heat" ? render(row) : null;

export const HeatQueuePage = () => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const { heat_override_mode: heatOverrideMode } = useTenantData();

  const { data, isLoading, error } = useGetHeatQueueQuery(
    { tenantName },
    { pollingInterval: POLLING_INTERVAL_MS }
  );

  // Supervisors get the write controls (select + edit + deploy); a viewer sees
  // the read-only queue only.
  const canEdit = usePermission(EDIT_HEAT_RECIPE_PERMISSIONS);
  const canViewCosts = usePermission([
    "crane_supervisor",
    "crane_supervisor_viewer",
  ]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const heats = useMemo(() => data?.heats ?? [], [data]);
  const {
    selectedHeats,
    selectableIds,
    toggleHeat,
    handleSelectionModelChange,
    clearSelection,
  } = useContiguousHeatSelection(heats);
  const overrides = useMemo(() => data?.active_overrides ?? [], [data]);
  const planUpdatedAt = data?.plan_updated_at ?? null;
  const rows = useMemo(() => buildRows(heats, overrides), [heats, overrides]);

  usePreloadOverrideRecipeModal({
    tenantName,
    enabled: canEdit && heatOverrideMode !== "delta",
  });
  useSummariseOverrideCosts({
    tenantName,
    enabled: canViewCosts && heatOverrideMode === "catalog",
  });

  const firstSelectedHeat = selectedHeats[0];
  const selectedGrade = firstSelectedHeat?.steel_grade ?? null;
  const selectedFloorRecipe: HeatRecipe | null =
    firstSelectedHeat?.floor_recipe ?? null;

  const restColumns = useHeatDataColumns<QueueRow>({
    heatCell,
    startTime: QUEUE_START_TIME,
  });

  const columns = useMemo((): GridColDef<QueueRow>[] => {
    const renderBanner = (row: OverrideStartRow | OverrideEndRow): ReactNode =>
      row.kind === "override-start" ? (
        <OverrideStartBanner
          override={row.override}
          heatCount={row.heatCount}
          claimedHeatIds={row.claimedHeatIds}
        />
      ) : (
        <OverrideEndBanner endHeatId={row.endHeatId} />
      );

    const statusColumn: GridColDef<QueueRow> = {
      field: "status",
      headerName: t("status"),
      width: 120,
      sortable: false,
      renderCell: heatCell((row) => <HeatStatusPill status={row.status} />),
    };

    // Override banner rows are a single cell spanning the whole table, carried
    // by the leading column (the checkbox column when present, else Status).
    const totalColumns = (canEdit ? 1 : 0) + 1 + restColumns.length;
    const bannerColSpan = (_value: unknown, row: QueueRow): number =>
      row.kind === "heat" ? 1 : totalColumns;

    if (!canEdit) {
      return [
        {
          ...statusColumn,
          align: "left",
          colSpan: bannerColSpan,
          renderCell: ({ row }: GridRenderCellParams<QueueRow>) =>
            row.kind === "heat" ? (
              <HeatStatusPill status={row.status} />
            ) : (
              renderBanner(row)
            ),
        },
        ...restColumns,
      ];
    }

    return [
      {
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
        renderHeader: () => null,
        align: "left",
        colSpan: bannerColSpan,
        renderCell: (params: GridRenderCellParams<QueueRow>) =>
          params.row.kind !== "heat"
            ? renderBanner(params.row)
            : GRID_CHECKBOX_SELECTION_COL_DEF.renderCell?.(params),
      },
      statusColumn,
      ...restColumns,
    ];
  }, [t, canEdit, restColumns]);

  return (
    <AccountPageLayout
      title={t("heatQueue")}
      actionButton={
        heatOverrideMode ===
          DELTA_OVERRIDES_TENANT_SETTINGS.heat_override_mode && canEdit ? (
          <Button
            component={Link}
            to={OVERRIDE_GUIDE_PATH}
            startIcon={<HelpOutlineRoundedIcon />}
          >
            {OVERRIDE_GUIDE_LINK_LABEL}
          </Button>
        ) : null
      }
    >
      {error && data === undefined ? (
        <Alert severity="error">{t("heatQueueLoadError")}</Alert>
      ) : (
        <Box>
          <MeltPlanStaleAlert planUpdatedAt={planUpdatedAt} sx={{ mb: 2 }} />
          <Stack
            direction="row"
            sx={{ alignItems: "center", gap: 2, mb: 2, minHeight: 36 }}
          >
            {canEdit && (
              <>
                <Button
                  variant="contained"
                  disabled={selectedHeats.length === 0}
                  onClick={() => {
                    setIsModalOpen(true);
                  }}
                >
                  {t("editRecipe")}
                </Button>
                {selectedHeats.length > 0 && (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {t("heatsSelected", { count: selectedHeats.length })}
                  </Typography>
                )}
              </>
            )}
            <Box sx={{ ml: "auto" }}>
              <MeltPlanUpdatedAt planUpdatedAt={planUpdatedAt} />
            </Box>
          </Stack>
          <HotkeyCompliantDataGrid
            columns={columns}
            rows={rows}
            loading={isLoading}
            autoHeight
            disableColumnSorting
            checkboxSelection={canEdit}
            keepNonExistentRowsSelected
            disableRowSelectionOnClick
            isRowSelectable={({ row }) =>
              row.kind === "heat" && selectableIds.has(row.stableHeatId)
            }
            rowSelectionModel={{
              type: "include",
              ids: new Set(selectedHeats.map((heat) => heat.heat_id)),
            }}
            onRowSelectionModelChange={handleSelectionModelChange}
            onRowClick={({ row }: GridRowParams<QueueRow>) => {
              if (
                canEdit &&
                row.kind === "heat" &&
                selectableIds.has(row.stableHeatId)
              ) {
                toggleHeat(row.stableHeatId);
              }
            }}
            rowHeight={40}
            getRowHeight={(params) =>
              (params.model as QueueRow).kind === "heat" ? null : 28
            }
            columnHeaderHeight={44}
            getRowClassName={({ row }) => {
              if (row.kind === "override-start") {
                return "heat-queue-override-start";
              }
              if (row.kind === "override-end") {
                return "heat-queue-override-end";
              }
              return row.status === "melting" || row.status === "loading"
                ? "heat-queue-active-row"
                : "";
            }}
            localeText={{ noRowsLabel: t("heatQueueEmpty") }}
            slotProps={{
              loadingOverlay: {
                variant: "skeleton",
                noRowsVariant: "skeleton",
              },
            }}
            sx={{
              ...HEAT_GRID_SX,
              [".heat-queue-active-row"]: { bgcolor: "action.hover" },
              [".heat-queue-override-start"]: { bgcolor: "action.hover" },
            }}
          />
        </Box>
      )}
      {heatOverrideMode === "delta" && isModalOpen && (
        <DeltaOverrideDialog
          heats={selectedHeats}
          onClose={() => {
            setIsModalOpen(false);
          }}
          onOverrideCreated={() => {
            clearSelection();
            setIsModalOpen(false);
          }}
        />
      )}
      {heatOverrideMode !== "delta" &&
        (selectedGrade !== null || isModalOpen) && (
          <OverrideRecipeModal
            open={isModalOpen}
            doClose={() => {
              setIsModalOpen(false);
            }}
            onOverrideCreated={() => {
              clearSelection();
              setIsModalOpen(false);
            }}
            selectedHeats={selectedHeats}
            steelGrade={selectedGrade ?? ""}
            floorRecipe={selectedFloorRecipe}
            heatTiming="upcoming"
          />
        )}
    </AccountPageLayout>
  );
};
